import {
  layerOutlineMm,
  polygonContainsPolygon,
  polygonArea,
  bboxOfPoints,
  type CanonicalDocument,
} from '@besbello/design-core';
import type { Diagnostic } from '../types.js';
import { ruleIdOf, type ValidationContext } from '../rules.js';

/**
 * La zona de marcaje manda. Un diseno que se sale no es "casi valido": es un
 * pedido que llega al taller y no se puede estampar.
 */
export function validateZone(doc: CanonicalDocument, ctx: ValidationContext): Diagnostic[] {
  const out: Diagnostic[] = [];
  const ruleId = ruleIdOf(ctx.technique);

  if (!ctx.zone.allowedTechniques.includes(ctx.technique.techniqueId)) {
    out.push({
      code: 'ZONE_TECHNIQUE_NOT_ALLOWED',
      severity: 'error',
      unit: 'none',
      ruleId: `zone:${ctx.zone.zoneId}`,
      params: { zone: ctx.zone.name, technique: ctx.technique.techniqueId },
    });
  }

  if (doc.layers.length === 0) {
    out.push({ code: 'EMPTY_DESIGN', severity: 'error', unit: 'none', ruleId: `zone:${ctx.zone.zoneId}` });
    return out;
  }

  if (ctx.technique.maxLayers !== null && doc.layers.length > ctx.technique.maxLayers) {
    out.push({
      code: 'LAYER_LIMIT_EXCEEDED',
      severity: 'error',
      measured: doc.layers.length,
      expected: ctx.technique.maxLayers,
      unit: 'count',
      ruleId,
    });
  }

  const canvas = [
    { x: 0, y: 0 },
    { x: doc.canvasMm.width, y: 0 },
    { x: doc.canvasMm.width, y: doc.canvasMm.height },
    { x: 0, y: doc.canvasMm.height },
  ];

  let totalArea = 0;
  for (const layer of doc.layers) {
    const outline = layerOutlineMm(layer);
    totalArea += polygonArea(outline.points.length >= 3 ? outline.points : []);

    if (!polygonContainsPolygon(ctx.zone.polygonMm, outline.points)) {
      // Cuanto se sale: la mayor distancia de un punto fuera al borde de la zona.
      const bbox = bboxOfPoints(outline.points);
      const zoneBox = bboxOfPoints(ctx.zone.polygonMm);
      const overflow = Math.max(
        zoneBox.minX - bbox.minX,
        zoneBox.minY - bbox.minY,
        bbox.maxX - zoneBox.maxX,
        bbox.maxY - zoneBox.maxY,
        0,
      );
      out.push({
        code: 'LAYER_OUTSIDE_ZONE',
        severity: 'error',
        layerId: layer.id,
        measured: round(overflow),
        expected: 0,
        unit: 'mm',
        ruleId: `zone:${ctx.zone.zoneId}`,
        params: { zone: ctx.zone.name, estimated: outline.estimated },
      });
    } else if (!polygonContainsPolygon(canvas, outline.points)) {
      out.push({
        code: 'LAYER_OUTSIDE_CANVAS',
        severity: 'warning',
        layerId: layer.id,
        unit: 'none',
        ruleId: `zone:${ctx.zone.zoneId}`,
      });
    }
  }

  if (ctx.technique.maxAreaMm2 !== null && totalArea > ctx.technique.maxAreaMm2) {
    out.push({
      code: 'DESIGN_AREA_EXCEEDED',
      severity: 'error',
      measured: round(totalArea),
      expected: ctx.technique.maxAreaMm2,
      unit: 'mm2',
      ruleId,
    });
  }

  return out;
}

/** Redondeo estable a 3 decimales: el diagnostico viaja por la red y se compara. */
function round(n: number): number {
  return Number(n.toFixed(3));
}
